import { useState } from 'react'
import classNames from 'classnames'
import { StencilCDNImage } from '@/components/Common/StencilImage'

export interface ImageProps {
  className?: string
  icon?: string
  width: number
  height: number
  altTag: string
  initialAlphabet?: string
  fallback_text?: string
  showNothingIfNoImageSrc?: boolean
}

export const ImageFallback = ({
  height = 50,
  width = 50,
  initialAlphabet,
  fallback_text,
}: {
  height: number
  width: number
  initialAlphabet?: string
  fallback_text?: string
}) => {
  // https://tailwindcss.com/docs/just-in-time-mode#arbitrary-value-support
  // Use inline styles for truly dynamic or user-defined values
  return (
    <div
      style={{ height: `${height}px`, width: `${width}px` }}
      className="mb-0 inline-block"
    >
      {initialAlphabet || fallback_text ? (
        <div
          className={classNames(
            'flex h-full w-full items-center justify-center rounded-full',
            {
              'bg-brand-blue-cerulean  text-16px text-white': initialAlphabet,
              'bg-grey-background text-brand-black text-13px font-semibold':
                fallback_text,
            },
          )}
          style={{
            minHeight: `${height}px`,
            minWidth: `${width}px`,
          }}
        >
          {/* in case initialAlphabet is more than a letter */}
          {fallback_text ?? initialAlphabet?.[0] ?? ''}
        </div>
      ) : (
        <div
          className="bg-brand-grey-background h-full w-full rounded-full"
          style={{ minHeight: `${height}px`, minWidth: `${width}px` }}
        />
      )}
    </div>
  )
}

const StencilImageWithFallback = ({
  className,
  icon,
  width,
  height,
  altTag,
  initialAlphabet,
  fallback_text,
  showNothingIfNoImageSrc,
}: ImageProps) => {
  const [imageErrored, setImageErrored] = useState(false)

  const handleImageError = () => setImageErrored(true)

  return icon ? (
    imageErrored ? (
      <ImageFallback
        width={width}
        height={height}
        initialAlphabet={initialAlphabet}
        fallback_text={fallback_text}
      />
    ) : (
      <StencilCDNImage
        className={`self-start ${className}`}
        width={width}
        height={height}
        src={icon}
        alt={altTag}
        onError={handleImageError}
      />
    )
  ) : showNothingIfNoImageSrc ? null : (
    <ImageFallback
      height={height}
      width={width}
      fallback_text={fallback_text}
    />
  )
}

export default StencilImageWithFallback
